'use client'

import { usePrevious } from '@mantine/hooks'
import { getVersionForClone } from '@qr-front/ui/utils'
import { useAtomValue } from 'jotai'
import { AnimatePresence, motion } from 'motion/react'

import { PhoneFrame } from '@/components/PhonePreview/PhoneFrame'
import { TPhonePreviewProps } from '@/components/PhonePreview/PhonePreviewTypes'

import { websiteUrlAtom } from '../../features/generator/contentFormSections/WebsiteUrlForm'
import { previewStatusBarColorAtom } from '../../features/generator/generatorState'
import { ContentWrapperWithTransition } from './ContentWrapperWithTransition'
import { DynamicIsland } from './DynamicIsland'
import { NavigationBar } from './NavigationBar'
import { PhoneButtons } from './PhoneButtons'
import { Placeholder } from './Placeholder'
import { PreviewStatusBar as BasePreviewStatusBar } from './PreviewStatusBar'

export const PHONE_PREVIEW_STATUS_BAR_BACKGROUND_SLOT_ID =
	'phone-preview-status-bar-background-slot'
export const PHONE_PREVIEW_OVERLAY_SLOT_ID = 'phone-preview-overlay-slot'
export const PHONE_PREVIEW_CONTENT_ID = 'phone-preview-content-id'

const PreviewStatusBar = getVersionForClone({
	base: BasePreviewStatusBar,
	clone1: () => <></>,
})

export const PhonePreview = ({
	content,
	placeholder = <Placeholder />,
	contentKey,
	isQrPreview,
}: TPhonePreviewProps) => {
	const statusBarColor = useAtomValue(previewStatusBarColorAtom)

	const isContentShown = !!content

	const showNavigationBar = isQrPreview || false
	const prevShowNavigationBar = usePrevious(showNavigationBar)
	const shouldAnimateEnterNavigationBar =
		showNavigationBar && prevShowNavigationBar === false
	const websiteUrl = useAtomValue(websiteUrlAtom)

	return (
		<PhoneFrame>
			<PhoneButtons />
			<AnimatePresence mode="wait">
				{!isContentShown ? (
					<motion.div
						key={`${isContentShown}`}
						initial={{ opacity: 1 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						{placeholder}
					</motion.div>
				) : null}
			</AnimatePresence>
			<AnimatePresence>
				{content ? (
					<ContentWrapperWithTransition
						content={
							<>
								{content}
								<div id={PHONE_PREVIEW_OVERLAY_SLOT_ID} />
							</>
						}
						contentKey={contentKey}
						isQrPreview={isQrPreview}
						id={PHONE_PREVIEW_CONTENT_ID}
						className="absolute left-0 top-0 h-full w-full overflow-hidden rounded-[32px] bg-black ring-1 ring-black"
					/>
				) : null}
			</AnimatePresence>
			<DynamicIsland />
			<AnimatePresence>
				{showNavigationBar ? (
					<motion.div
						initial={{
							opacity: showNavigationBar
								? shouldAnimateEnterNavigationBar
									? 0
									: 1
								: 0,
						}}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						<NavigationBar url={websiteUrl} />
					</motion.div>
				) : null}
			</AnimatePresence>
			<PreviewStatusBar color={statusBarColor} />
		</PhoneFrame>
	)
}
